import { fetchUser, UserData, UserPermissions, UserRole } from '@nextforce/api'
import { useQuery } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { RetryQuery } from 'shared/queries/utils'
import keys from './shared/keys'

export type UserWithPermissions = UserData & { permissions?: UserPermissions }

export function useUser(retryQuery?: RetryQuery, companyId?: string) {
  const { server } = useServer()
  return useQuery<UserWithPermissions>({
    queryKey: keys.detail(companyId),
    queryFn: fetchUserWithPermissions,
    retry: retryQuery ?? false,
  })

  async function fetchUserWithPermissions(): Promise<UserWithPermissions> {
    const response = await fetchUser(server, companyId)
    if (response.role) {
      response.permissions = getUserPermissionsByRole(response.role)
    }
    return response
  }
}

const BASE_PERMISSIONS: UserPermissions = {
  canSeeTools: false,
  canSeeNextShifts: false,
  canSeeHelperSchedule: false,
  canSeeStoreSchedule: false,
  canSeeNotifications: false,
  canSeeMonthlySummary: false,
  canApproveTimeOffRequests: false,
  canEditCustomEvents: false,
  showGroupedConversations: false,
  showStoreDropdown: false,
  canSeeAnnouncements: false,
  canSeeMessages: false,
  canEditHolidayEvents: false,
  canEditCustomTasks: false,
}

const GUEST_PERMISSIONS: UserPermissions = {
  ...BASE_PERMISSIONS,
  canSeeNotifications: true,
  canSeeTools: true,
  canSeeMessages: true,
}

const STAFF_PERMISSIONS: UserPermissions = {
  ...GUEST_PERMISSIONS,
  showGroupedConversations: true,
  canSeeAnnouncements: true,
}

const HELPER_PERMISSIONS: UserPermissions = {
  ...STAFF_PERMISSIONS,
  showGroupedConversations: false,
  canSeeHelperSchedule: true,
  canSeeNextShifts: true,
  canSeeMonthlySummary: true,
  canSeeTools: false,
}

const MANAGER_PERMISSIONS: UserPermissions = {
  ...HELPER_PERMISSIONS,
  canSeeStoreSchedule: true,
  canSeeHelperSchedule: false,
  canSeeTools: true,
  canSeeNextShifts: false,
  canSeeMonthlySummary: false,
}

const OBSERVER_PERMISSIONS: UserPermissions = {
  ...MANAGER_PERMISSIONS,
  showStoreDropdown: true,
  showGroupedConversations: true,
  canSeeMonthlySummary: false,
  canEditCustomEvents: true,
  canApproveTimeOffRequests: true,
  canEditHolidayEvents: true,
  canEditCustomTasks: true,
}

const ADMIN_PERMISSIONS: UserPermissions = {
  ...OBSERVER_PERMISSIONS,
}

function getUserPermissionsByRole(role: UserRole): UserPermissions {
  switch (role) {
    case 'admin':
      return ADMIN_PERMISSIONS
    case 'observer':
      return OBSERVER_PERMISSIONS
    case 'manager':
      return MANAGER_PERMISSIONS
    case 'helper':
      return HELPER_PERMISSIONS
    case 'staff':
      return STAFF_PERMISSIONS
    case 'guest':
      return GUEST_PERMISSIONS
    default:
      return BASE_PERMISSIONS
  }
}
